import { computed, ref } from 'vue';

import { NotificationService } from '@/shared/api';

import { ProjectSettings } from '../context';

enum PushNotificationsPermission {
  DEFAULT = 'default',
  DENIED = 'denied',
  GRANTED = 'granted',
  UNSUPPORTED = 'unsupported',
}

const toApplicationServerKey = (value: string): Uint8Array<ArrayBuffer> => {
  const base64: string = (value + '='.repeat((4 - (value.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  return Uint8Array.from(window.atob(base64), (character: string) => character.charCodeAt(0));
};

type PushSubscriptionPayload = {
  endpoint: string;
  keys: {
    auth: string;
    p256dh: string;
  };
};

const getSubscriptionPayload = (subscription: PushSubscription): PushSubscriptionPayload | null => {
  const payload: PushSubscriptionJSON = subscription.toJSON();
  if (!payload.endpoint || !payload.keys?.auth || !payload.keys.p256dh) {
    return null;
  }

  return {
    endpoint: payload.endpoint,
    keys: { auth: payload.keys.auth, p256dh: payload.keys.p256dh },
  };
};

const getOrCreateSubscription = async (): Promise<PushSubscription> => {
  const registration: ServiceWorkerRegistration = await navigator.serviceWorker.ready;
  const existingSubscription: PushSubscription | null =
    await registration.pushManager.getSubscription();

  if (existingSubscription) {
    return existingSubscription;
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: toApplicationServerKey(import.meta.env.VAPID_PUBLIC_KEY),
  });
};

const registerSubscription = async (payload: PushSubscriptionPayload): Promise<string | null> => {
  const result = await NotificationService.registerPushSubscription({ body: payload });

  if ('error' in result && result.error) {
    return null;
  }

  return result.data?.id ?? null;
};

const subscribeCurrentUser = async (): Promise<string | null> => {
  try {
    const subscription: PushSubscription = await getOrCreateSubscription();
    const payload: PushSubscriptionPayload | null = getSubscriptionPayload(subscription);

    return payload ? registerSubscription(payload) : null;
  } catch {
    return null;
  }
};

const createPushNotificationsService = () => {
  const permission = ref<PushNotificationsPermission>(PushNotificationsPermission.UNSUPPORTED);
  const pushEnabled = ref<boolean>(false);
  const showMessagePreview = ref<boolean>(false);
  const hasRegistrationError = ref<boolean>(false);

  let isInitialized: boolean = false;
  let setEnabledPromise: Promise<void> = Promise.resolve();
  let subscriptionId: string | null = null;
  let activeUserId: string | null = null;
  let synchronizationPromise: Promise<void> = Promise.resolve();

  const isSupported = computed<boolean>(
    () =>
      ProjectSettings.pushNotifications.enabled &&
      import.meta.env.VAPID_PUBLIC_KEY.length > 0 &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window,
  );

  const enable = async (): Promise<boolean> => {
    if (!isSupported.value) {
      return false;
    }

    const nextPermission: NotificationPermission = await Notification.requestPermission();
    permission.value = nextPermission as PushNotificationsPermission;
    if (nextPermission !== 'granted') {
      return false;
    }

    const nextSubscriptionId: string | null = await subscribeCurrentUser();
    if (!nextSubscriptionId) {
      hasRegistrationError.value = true;
      return false;
    }

    subscriptionId = nextSubscriptionId;

    const settings = await NotificationService.updateUserNotificationSettings({
      body: { pushEnabled: true, showMessagePreview: showMessagePreview.value },
    });
    if ('error' in settings && settings.error) {
      hasRegistrationError.value = true;
      return false;
    }

    pushEnabled.value = true;
    hasRegistrationError.value = false;
    return true;
  };

  const disable = async (): Promise<boolean> => {
    const result = await NotificationService.updateUserNotificationSettings({
      body: { pushEnabled: false, showMessagePreview: showMessagePreview.value },
    });
    if ('error' in result && result.error) {
      return false;
    }

    pushEnabled.value = false;
    hasRegistrationError.value = false;
    return true;
  };

  const setShowMessagePreview = async (value: boolean): Promise<void> => {
    const result = await NotificationService.updateUserNotificationSettings({
      body: { pushEnabled: pushEnabled.value, showMessagePreview: value },
    });
    if (!('error' in result && result.error)) {
      showMessagePreview.value = value;
    }
  };

  const onServiceWorkerMessage = (event: MessageEvent<{ type?: string }>): void => {
    if (event.data.type === 'push-subscription-change' && pushEnabled.value) {
      void enable();
    }
  };

  const registerCurrentUserSubscription = async (): Promise<boolean> => {
    const nextSubscriptionId: string | null = await subscribeCurrentUser();
    if (!nextSubscriptionId) {
      return false;
    }

    subscriptionId = nextSubscriptionId;
    return true;
  };

  const clearState = (): void => {
    isInitialized = false;
    activeUserId = null;
    pushEnabled.value = false;
    showMessagePreview.value = false;
    hasRegistrationError.value = false;
  };

  const retryCurrentUserSubscription = (): void => {
    if (activeUserId) {
      void sync(activeUserId);
    }
  };

  const deactivateCurrentUser = async (): Promise<void> => {
    if (isInitialized) {
      navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
      window.removeEventListener('online', retryCurrentUserSubscription);
    }

    const currentSubscriptionId: string | null = subscriptionId;
    subscriptionId = null;

    if (currentSubscriptionId) {
      await NotificationService.deletePushSubscription({
        path: { subscriptionId: currentSubscriptionId },
      });
    }

    clearState();
  };

  const activateCurrentUser = async (userId: string): Promise<void> => {
    if (!isSupported.value) {
      return;
    }

    const isSameUser: boolean = activeUserId === userId;
    if (activeUserId && activeUserId !== userId) {
      await deactivateCurrentUser();
    }

    activeUserId = userId;
    isInitialized = true;
    permission.value = Notification.permission as PushNotificationsPermission;
    if (!isSameUser) {
      navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);
      window.addEventListener('online', retryCurrentUserSubscription);
    }

    const result = await NotificationService.getUserNotificationSettings();
    if ('error' in result && result.error) {
      hasRegistrationError.value = true;
      return;
    }

    pushEnabled.value = result.data?.pushEnabled ?? false;
    showMessagePreview.value = result.data?.showMessagePreview ?? false;

    if (pushEnabled.value && permission.value === PushNotificationsPermission.GRANTED) {
      hasRegistrationError.value = !(await registerCurrentUserSubscription());
    } else {
      hasRegistrationError.value = false;
    }
  };

  const sync = (userId: string | null): Promise<void> => {
    const nextSynchronization: Promise<void> = synchronizationPromise
      .catch((): void => undefined)
      .then(async (): Promise<void> => {
        if (userId) {
          await activateCurrentUser(userId);
        } else {
          await deactivateCurrentUser();
        }
      });

    synchronizationPromise = nextSynchronization;
    return nextSynchronization;
  };

  return {
    permission: computed<PushNotificationsPermission>(() => permission.value),
    showMessagePreview: computed<boolean>(() => showMessagePreview.value),
    isEnabled: computed<boolean>(
      () => permission.value === PushNotificationsPermission.GRANTED && pushEnabled.value,
    ),
    hasRegistrationError: computed<boolean>(() => hasRegistrationError.value),
    isSupported,
    sync,
    enable,
    disable,
    setEnabled: (value: boolean): Promise<void> => {
      const nextSetEnabledPromise: Promise<void> = setEnabledPromise
        .catch((): void => undefined)
        .then(async (): Promise<void> => {
          if (value) {
            await enable();
          } else {
            await disable();
          }
        });
      setEnabledPromise = nextSetEnabledPromise;

      return nextSetEnabledPromise;
    },
    setShowMessagePreview,
  };
};

const pushNotificationsService = createPushNotificationsService();

export { PushNotificationsPermission, pushNotificationsService };
